import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, Package, Calendar, Eye, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import ImageUpload from '@/components/ImageUpload';

export default function ExtensionList() {
  const [extensions, setExtensions] = useState([]);
  const [tcgs, setTcgs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', tcg_id: '', release_date: '', preorder_deadline: '', product_type: 'tcg', image_url: '' });

  const load = () => api.get('/admin/extensions').then(r => setExtensions(r.data));
  useEffect(() => { load(); api.get('/admin/tcgs').then(r => setTcgs(r.data)); }, []);

  const openCreate = () => { setForm({ name: '', description: '', tcg_id: '', release_date: '', preorder_deadline: '', product_type: 'tcg', image_url: '' }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.name || !form.release_date || !form.preorder_deadline) { toast.error("Nom, date de sortie et date limite sont requis"); return; }
    try {
      await api.post('/admin/extensions', { ...form, tcg_id: form.tcg_id || null });
      toast.success("Extension créée");
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async (ext) => {
    if (!window.confirm(`Supprimer "${ext.name}" et toutes ses précommandes ?`)) return;
    try { await api.delete(`/admin/extensions/${ext.id}`); toast.success("Extension supprimée"); load(); }
    catch (err) { toast.error("Erreur lors de la suppression"); }
  };

  const statusColors = { ouvert: 'bg-emerald-100 text-emerald-800 border-emerald-200', ferme: 'bg-rose-100 text-rose-800 border-rose-200', livre: 'bg-sky-100 text-sky-800 border-sky-200' };
  const statusLabels = { ouvert: 'Ouvert', ferme: 'Fermé', livre: 'Livré' };
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

  return (
    <div data-testid="extension-list">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Extensions</h1>
          <p className="text-slate-500 mt-1">Gérez vos extensions et jeux</p>
        </div>
        <Button className="rounded-full" onClick={openCreate} data-testid="create-extension-btn">
          <Plus className="h-4 w-4 mr-2" /> Nouvelle extension
        </Button>
      </div>

      {extensions.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
          <Package className="h-12 w-12 mx-auto text-slate-200" />
          <p className="mt-4 text-slate-500">Aucune extension créée</p>
          <Button className="mt-4 rounded-full" onClick={openCreate}>Créer une extension</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {extensions.map(ext => (
            <div key={ext.id} className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-shadow" data-testid={`ext-card-${ext.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <Badge className={`text-xs border ${statusColors[ext.status] || statusColors.ouvert}`}>{statusLabels[ext.status] || ext.status}</Badge>
                    {ext.tcg_name && <Badge variant="secondary" className="text-xs">{ext.tcg_name}</Badge>}
                  </div>
                  <h3 className="font-semibold text-slate-900 truncate">{ext.name}</h3>
                  <div className="mt-2 space-y-1 text-sm text-slate-500">
                    <div className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />Sortie : {formatDate(ext.release_date)}</div>
                    <div className="flex items-center gap-1.5"><Package className="h-3.5 w-3.5" />{ext.items?.length || 0} produits</div>
                    <div className="flex items-center gap-1.5"><ShoppingBag className="h-3.5 w-3.5" />{ext.preorder_count || 0} précommandes</div>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                <Link to={`/admin/extensions/${ext.id}`} className="flex-1">
                  <Button variant="outline" size="sm" className="w-full rounded-full text-xs" data-testid={`manage-ext-${ext.id}`}>
                    <Eye className="h-3 w-3 mr-1" /> Gérer
                  </Button>
                </Link>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500 hover:bg-rose-50" onClick={() => handleDelete(ext)} data-testid={`delete-ext-${ext.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="extension-dialog">
          <DialogHeader><DialogTitle>Nouvelle extension</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            <div><Label className="text-xs font-medium text-slate-500">Nom</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1 rounded-lg" data-testid="ext-name-input" /></div>
            <div><Label className="text-xs font-medium text-slate-500">Description</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1 rounded-lg" /></div>
            <div><Label className="text-xs font-medium text-slate-500">Type de produit</Label>
              <Select value={form.product_type} onValueChange={v => setForm(f => ({ ...f, product_type: v }))}>
                <SelectTrigger className="mt-1 rounded-lg" data-testid="ext-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="tcg">TCG</SelectItem><SelectItem value="jeu_de_societe">Jeu de société</SelectItem><SelectItem value="bd_manga">BD & Manga</SelectItem></SelectContent>
              </Select>
            </div>
            {form.product_type === 'tcg' && (
              <div><Label className="text-xs font-medium text-slate-500">TCG</Label>
                <Select value={form.tcg_id} onValueChange={v => setForm(f => ({ ...f, tcg_id: v }))}>
                  <SelectTrigger className="mt-1 rounded-lg" data-testid="ext-tcg-select"><SelectValue placeholder="Sélectionner un TCG" /></SelectTrigger>
                  <SelectContent>{tcgs.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-medium text-slate-500">Date de sortie</Label><Input type="date" value={form.release_date} onChange={e => setForm(f => ({ ...f, release_date: e.target.value }))} className="mt-1 rounded-lg" data-testid="ext-release-input" /></div>
              <div><Label className="text-xs font-medium text-slate-500">Date limite précommande</Label><Input type="date" value={form.preorder_deadline} onChange={e => setForm(f => ({ ...f, preorder_deadline: e.target.value }))} className="mt-1 rounded-lg" data-testid="ext-deadline-input" /></div>
            </div>
            <ImageUpload value={form.image_url} onChange={v => setForm(f => ({ ...f, image_url: v }))} label="Visuel de l'extension" testId="ext-image" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Annuler</Button>
            <Button onClick={handleSave} className="rounded-full" data-testid="ext-save-btn">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
